import { useState, useEffect, useCallback } from "react";
import { useUser } from "./useUser";
import { Agent } from "../types/booking";
import { fetchAgentUserByUserId } from "../services/agentUsersService";

type AgentProfileCache = {
  userId: string;
  agentId: number | null;
  agent: Agent | null;
};

/** Survives route changes within the agent portal so pages don't re-block on profile load. */
let profileCache: AgentProfileCache | null = null;
let profileInflight: Promise<AgentProfileCache | null> | null = null;

const loadProfile = async (userId: string): Promise<AgentProfileCache | null> => {
  if (profileCache?.userId === userId) return profileCache;
  if (profileCache && profileCache.userId !== userId) profileCache = null;
  if (profileInflight) return profileInflight;

  profileInflight = (async () => {
    const { data } = await fetchAgentUserByUserId(userId);
    if (!data) {
      profileCache = { userId, agentId: null, agent: null };
      return profileCache;
    }
    const agentRecord = data.agent;
    const agent = agentRecord
      ? Array.isArray(agentRecord)
        ? agentRecord[0]
        : agentRecord
      : null;
    profileCache = { userId, agentId: data.agent_id, agent };
    return profileCache;
  })().finally(() => {
    profileInflight = null;
  });

  return profileInflight;
};

export const useAgentProfile = () => {
  const { user } = useUser();
  const cached =
    user?.id && profileCache?.userId === user.id ? profileCache : null;
  const [agentId, setAgentId] = useState<number | null>(
    cached?.agentId ?? null
  );
  const [agent, setAgent] = useState<Agent | null>(cached?.agent ?? null);
  const [loading, setLoading] = useState(!cached);

  const refresh = useCallback(async () => {
    if (!user?.id) {
      profileCache = null;
      setAgentId(null);
      setAgent(null);
      setLoading(false);
      return;
    }

    // Only show loading spinner when we have no cached profile for this user
    if (!(profileCache?.userId === user.id)) {
      setLoading(true);
    }

    const next = await loadProfile(user.id);
    if (next) {
      setAgentId(next.agentId);
      setAgent(next.agent);
    } else {
      setAgentId(null);
      setAgent(null);
    }
    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { agentId, agent, loading, refresh };
};
